import React from 'react';
import { DatePicker, InformationTip, SingleLineText } from '@bloomays-lib/ui.shared';
import { Upload } from './Upload';
import { styled } from '@mui/material/styles';
import type { BrowserUploadFile } from '@bloomays-lib/types.shared';
import type { FileRejection } from 'react-dropzone';
import RawHelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { DateValidationError } from '@mui/x-date-pickers';
import { dateFormat } from '@bloomays-lib/utils.shared';

type ExpirationUploadFieldProps = {
  title: string;
  uploadAriaLabel: string;
  uploadId: string;
  uploadName?: string;
  uploadPlaceholder: string;
  uploadTextLabel: string;
  uploadRequired: boolean;
  preloadedFiles: BrowserUploadFile[];
  onUploadChange: (files: BrowserUploadFile[], rejected: FileRejection[]) => void;
  fileTypes?: string[];
  maxFiles?: number;
  maxLength?: number;
  uploadAutoload?: boolean;
  uploadDisplayIcons?: boolean;
  uploadRef?: React.Ref<HTMLDivElement>;

  expirationLabel: string;
  expirationValue?: Date;
  onExpirationChange: (value: string) => void;
  expirationError?: string;
  onExpirationErrorClear?: () => void;
  shouldDisableDate?: (date: Date) => boolean;
  minDate?: Date;
  maxDate?: Date;
  infoContent?: React.ReactNode;
};

const ExpirationUploadField = ({
  title,
  uploadAriaLabel,
  uploadId,
  uploadName,
  uploadPlaceholder,
  uploadTextLabel,
  uploadRequired,
  preloadedFiles,
  onUploadChange,
  fileTypes,
  maxFiles = 1,
  maxLength = 5000000,
  uploadAutoload = true,
  uploadDisplayIcons = true,
  uploadRef,
  expirationLabel,
  expirationValue,
  onExpirationChange,
  expirationError,
  onExpirationErrorClear,
  shouldDisableDate,
  minDate,
  maxDate,
  infoContent,
}: ExpirationUploadFieldProps) => {
  const [dateError, setDateError] = React.useState<DateValidationError | null>(null);
  const errorMessage = React.useMemo(() => {
    if (dateError === 'maxDate' || dateError === 'minDate') {
      return `Veuillez choisir une date entre le ${dateFormat(
        minDate || new Date(),
        'dd/MM/yyyy',
        'fr',
      )} et le ${dateFormat(maxDate || new Date(), 'dd/MM/yyyy', 'fr')}`;
    }

    if (dateError === 'invalidDate') {
      return 'Veuillez choisir une date valide';
    }

    if (expirationError) {
      return expirationError;
    }

    return '';
  }, [dateError, minDate, maxDate, expirationError]);
  return (
    <FileContainer>
      <h4>
        {title}
        {infoContent && (
          <InformationTip>
            <ContainerQuestion>
              <HelpOutlineIcon />
              <ContainerInfotips>
                <SingleLineText text={infoContent} />
              </ContainerInfotips>
            </ContainerQuestion>
          </InformationTip>
        )}
      </h4>
      <Upload
        aria-label={uploadAriaLabel}
        ref={uploadRef}
        id={uploadId}
        placeholder={uploadPlaceholder}
        textLabel={uploadTextLabel}
        name={uploadName}
        fileTypes={fileTypes}
        displayIcons={uploadDisplayIcons}
        maxFiles={maxFiles}
        maxLength={maxLength}
        preloadedFiles={preloadedFiles}
        handleChange={onUploadChange}
        autoload={uploadAutoload}
        required={uploadRequired}
      />
      <DatePicker
        value={expirationValue ? expirationValue : null}
        handleChange={(date): void => {
          if (!date) return;
          if (onExpirationErrorClear) {
            onExpirationErrorClear();
          }
          onExpirationChange(date.toISOString());
        }}
        label={expirationLabel}
        minDate={minDate}
        maxDate={maxDate}
        shouldDisableDate={shouldDisableDate}
        sx={{ marginTop: '1em' }}
        errorLabel={errorMessage}
        onError={(error) => {
          setDateError(error);
        }}
      />
    </FileContainer>
  );
};

export default ExpirationUploadField;
export { ExpirationUploadField };

const FileContainer = styled('div')(({ theme }) => ({
  border: `2px solid ${theme.palette.grey[400]}`,
  borderRadius: '8px',
  padding: '1em',
  marginBottom: '1em',
  textAlign: 'left',
}));

const ContainerInfotips = styled('div')(() => ({
  display: 'flex',
  flexFlow: 'row nowrap',
  alignItems: 'center',
}));

const ContainerQuestion = styled('div')(({ theme }) => ({
  marginBottom: '1em',
  textAlign: 'center',
  padding: '1em',
}));

const HelpOutlineIcon = styled(RawHelpOutlineIcon)(() => ({
  paddingRight: 10,
}));
